import React, { useEffect, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";

/**
 * Cinematic 21:9 HD video player for the PANDI section.
 * - Aspect-locked 21:9 container (object-fit: cover for the 16:9 source)
 * - Auto-plays with sound when scrolled into view; auto-pauses when out of view.
 * - Tap the player to toggle play/pause. Custom HUD: seek bar + fullscreen only.
 */
export default function PandiVideo({
  srcMp4 = "/videos/soori_pandi_web.mp4",
  srcWebm = "/videos/soori_pandi.webm",
  poster = "/videos/soori_pandi_poster.jpg",
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showCtrls, setShowCtrls] = useState(true);
  const hideTimer = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Set iOS-only playback attributes via DOM (React strips unknown JSX props).
    v.setAttribute('webkit-playsinline', 'true');
    v.setAttribute('x5-playsinline', 'true');

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTime = () => {
      setProgress(v.currentTime);
      setDuration(v.duration || 0);
    };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("timeupdate", onTime);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("timeupdate", onTime);
    };
  }, []);

  // Lazy-load + auto-play/pause with sound. On iOS a 54 MB MP4 with
  // preload="auto" hangs the page during scroll, so we:
  //  1) start with preload="none" and no <source> src on the video element
  //  2) when the user gets within ~600 px of the section, attach the sources
  //     and call load() — this fetches in the background while they scroll
  //  3) when the player is 60%+ visible, call play() (muted first, then unmute)
  //  4) when < 35% visible, pause
  // We rely ONLY on IntersectionObserver (no scroll listener) because
  // getBoundingClientRect() on every scroll frame is what freezes mobile Safari.
  useEffect(() => {
    const v = videoRef.current;
    const el = containerRef.current;
    if (!v || !el) return;

    let loaded = false;

    const attachSources = () => {
      if (loaded) return;
      loaded = true;
      // Insert MP4 first (iOS-friendly), WebM as fallback for Chromium desktop.
      const s1 = document.createElement('source');
      s1.src = srcMp4;
      s1.type = 'video/mp4';
      const s2 = document.createElement('source');
      s2.src = srcWebm;
      s2.type = 'video/webm';
      v.appendChild(s1);
      v.appendChild(s2);
      v.preload = 'auto';
      try { v.load(); } catch (_) { /* noop */ }
    };

    const enter = async () => {
      attachSources();
      try {
        await v.play();
        try { v.muted = false; } catch (_) { /* iOS volume */ }
      } catch (_) { /* blocked */ }
    };

    // Near-viewport observer: prefetches the video when the user is close.
    const nearIO = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) attachSources(); },
      { rootMargin: '600px 0px' }
    );
    nearIO.observe(el);

    // In-viewport observer: controls play/pause with hysteresis.
    const playIO = new IntersectionObserver(
      ([entry]) => {
        const r = entry.intersectionRatio;
        if (r >= 0.6) enter();
        else if (r < 0.35 && !v.paused) v.pause();
      },
      { threshold: [0, 0.1, 0.35, 0.5, 0.6, 0.75, 1] }
    );
    playIO.observe(el);

    // Pause when the tab is hidden.
    const onVis = () => { if (document.hidden && !v.paused) v.pause(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      nearIO.disconnect();
      playIO.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [srcMp4, srcWebm]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      // Start playback first; only after it resolves try to unmute.
      const p = v.play();
      if (p && p.then) {
        p.then(() => {
          try { v.muted = false; } catch (_) { /* iOS volume */ }
        }).catch(() => { /* blocked */ });
      }
    } else {
      v.pause();
    }
  };

  const goFullscreen = (e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    const el = containerRef.current;
    if (!v || !el) return;

    // Exit if already fullscreen
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
      return;
    }

    // iOS Safari: only the <video> element supports fullscreen via webkitEnterFullscreen.
    if (typeof v.webkitEnterFullscreen === 'function') {
      try {
        // iOS will use the system video player; ensure sound on.
        v.muted = false;
        v.webkitEnterFullscreen();
        return;
      } catch (_) { /* fall through */ }
    }

    // Standard Fullscreen API — try the container first (keeps custom HUD),
    // then fall back to the video element.
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      v.requestFullscreen ||
      v.webkitRequestFullscreen;
    if (req) {
      try {
        const target = el.requestFullscreen || el.webkitRequestFullscreen ? el : v;
        (el.requestFullscreen || el.webkitRequestFullscreen).call(target);
      } catch (_) { /* swallow */ }
    }
  };

  const onSeek = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = Math.max(0, Math.min(duration, pct * duration));
  };

  const onPointerMove = () => {
    setShowCtrls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (playing) setShowCtrls(false);
    }, 1000);
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-black overflow-hidden group select-none"
      style={{ aspectRatio: "21 / 9" }}
      onClick={togglePlay}
      onMouseMove={onPointerMove}
      onTouchStart={onPointerMove}
      data-testid="pandi-video"
    >
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full"
        style={{ objectFit: "cover", objectPosition: "center 30%" }}
        loop
        muted
        playsInline
        preload="none"
        poster={poster}
      />

      {/* Gradient scrim — only when controls visible */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          opacity: showCtrls ? 1 : 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, transparent 25%, transparent 60%, rgba(0,0,0,0.78) 100%)",
        }}
      />

      {/* Top-right HUD */}
      <div
        className="absolute top-3 right-3 flex items-center gap-2 transition-opacity duration-300"
        style={{ opacity: showCtrls ? 1 : 0 }}
      >
        <span className="font-sc text-[10px] gold-text tracking-[0.4em] bg-black/40 backdrop-blur-sm px-2 py-1 border border-[color:var(--bx-gold)]/30">
          {"HD \u00B7 21:9"}
        </span>
      </div>

      {/* Top-left mood tag */}
      <div
        className="absolute top-3 left-3 flex items-center gap-2 transition-opacity duration-300"
        style={{ opacity: showCtrls ? 1 : 0 }}
      >
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="font-mono text-[10px] text-[color:var(--bx-bone)]/85">{"PANDI \u00B7 REEL"}</span>
      </div>

      {/* Bottom control strip */}
      <div
        className="absolute left-0 right-0 bottom-0 px-4 pb-3 pt-6 transition-opacity duration-300"
        style={{ opacity: showCtrls ? 1 : 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Seek bar */}
        <div
          className="w-full h-[3px] bg-white/20 cursor-pointer mb-3 relative"
          onClick={onSeek}
        >
          <div
            className="h-full bg-[color:var(--bx-gold)] absolute left-0 top-0"
            style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
          />
          <div
            className="w-3 h-3 rounded-full bg-[color:var(--bx-gold)] absolute -top-[5px]"
            style={{
              left: `calc(${duration ? (progress / duration) * 100 : 0}% - 6px)`,
              boxShadow: "0 0 12px rgba(200,161,74,0.7)",
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[11px] text-[color:var(--bx-bone)]/85">
            {fmt(progress)} / {fmt(duration)}
          </span>
          <button
            type="button"
            onClick={goFullscreen}
            className="text-[color:var(--bx-bone)] hover:text-[color:var(--bx-gold)] transition-colors p-1"
            aria-label="Fullscreen"
            data-testid="pandi-fullscreen-btn"
          >
            <Maximize2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
